import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Toolbar from '@mui/material/Toolbar';
import * as React from 'react';
import { useSelector } from 'react-redux';
import logout from '../../store/modules/auth/logout';
import ZoomControl from '../layout/ZoomControl';
import Logo from './Logo';
import Menus from './Menu';
import MenuHorizontal from './MenuHorizontal';

function ResponsiveAppBar() {
  const [userName, setUserName] = React.useState('');
  const firstLetter = userName ? userName.charAt(0).toUpperCase() : '';
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const profileId = useSelector((state: any) => state.auth.profileId);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  React.useEffect(() => {
    const storedUserName = localStorage.getItem('name');
    if (storedUserName) {
      const nameParts = storedUserName.split(' ');
      setUserName(nameParts[0]);
    }
  }, [isLoggedIn]);

  return (
    <div>
      {isLoggedIn && (
        <AppBar
          position="fixed"
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background:
                'linear-gradient(90deg, #fbbf24 0%, #f59e0b 50%, #10b981 100%)',
              zIndex: 1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.03) 50%, transparent 70%)',
              transform: 'translateX(-100%)',
              transition: 'transform 3s ease-in-out',
              zIndex: 0,
              animation: 'shimmer 6s ease-in-out infinite',
            },
            '@keyframes shimmer': {
              '0%': { transform: 'translateX(-100%)' },
              '50%': { transform: 'translateX(100%)' },
              '100%': { transform: 'translateX(-100%)' },
            },
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{
                position: 'relative',
                zIndex: 2,
                minHeight: { xs: '64px', sm: '70px' },
              }}
            >
              {/* Mobile Menu Button */}
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: 'flex', sm: 'none', md: 'none' },
                }}
              >
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  sx={{
                    color: '#ffffff',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <MenuIcon sx={{ fontSize: '1.5rem' }} />
                </IconButton>

                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiPaper-root': {
                      background:
                        'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: '16px',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                      marginTop: '8px',
                    },
                  }}
                >
                  <Menus logout={logout} />
                </Menu>
              </Box>

              {/* Logo */}
              <Logo />

              {/* Menu Horizontal (Desktop) */}
              {profileId === 2 ? (
                <MenuHorizontal />
              ) : (
                <Box
                  sx={{
                    width: '80%',
                    display: { xs: 'none', sm: 'flex', md: 'flex' },
                    gap: '1rem',
                    justifyContent: 'center',
                  }}
                />
              )}

              {/* User Profile Section */}
              <Box
                sx={{
                  flexGrow: 0,
                  display: { xs: 'none', sm: 'flex', md: 'flex' },
                  marginRight: { sm: '1rem', md: '2rem', lg: '3rem' },
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    marginRight: '0.8rem',
                  }}
                >
                  <ZoomControl version="1.3.0" />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '6px 16px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                  onClick={handleOpenUserMenu}
                >
                  <IconButton sx={{ p: 0 }}>
                    <Avatar
                      sx={{
                        background:
                          'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        width: '30px',
                        height: '30px',
                        fontSize: '12pt',
                        fontFamily: 'Roboto-Medium',
                        color: '#ffffff',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                      alt={firstLetter}
                    >
                      {firstLetter || <PersonIcon sx={{ fontSize: '20px' }} />}
                    </Avatar>
                  </IconButton>

                  <Typography
                    sx={{
                      marginLeft: '12px',
                      fontSize: '12pt',
                      fontFamily: 'Roboto-Medium',
                      color: '#ffffff',
                      display: { sm: 'none', md: 'flex' },
                      alignItems: 'center',
                      letterSpacing: '0.3px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      userSelect: 'none',
                    }}
                  >
                    {userName || 'Usuário'}
                  </Typography>
                </Box>

                <Menu
                  sx={{
                    mt: '50px',
                    '& .MuiPaper-root': {
                      background:
                        'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: '16px',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                      boxShadow: `
                        0 4px 6px rgba(0, 0, 0, 0.08),
                        0 8px 12px rgba(0, 0, 0, 0.06),
                        0 16px 24px rgba(0, 0, 0, 0.04)
                      `,
                      marginTop: '8px',
                    },
                  }}
                  id="menu-appbar-user"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <Menus logout={logout} />
                </Menu>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      )}
    </div>
  );
}

export default ResponsiveAppBar;
